import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PartnerListingDataContributionWizard from "../../components/partner/PartnerListingDataContributionWizard";
import { usePartnerPortalBase } from "../../components/partner/PartnerPortalBaseProvider";
import { useLocale } from "../../intl";
import {
  fetchCasaMapaPartnerAgencies,
  fetchCasaMapaPartnerDataContributions,
  fetchCasaMapaPartnerListingSync,
  updateCasaMapaPartnerListingSync,
} from "../../services/casamapaPartnerAgencyApi";
import PartnerCasaMapaSettingsShell from "./PartnerCasaMapaSettingsShell";
import {
  formatPartnerSettingsWhen,
  PARTNER_SETTINGS_COPY as COPY,
  partnerSettingsStatusBadgeClass,
} from "./partnerCasaMapaSettingsShared";

export default function PartnerCasaMapaInventorySettingsPage() {
  const { t } = useLocale();
  const { path } = usePartnerPortalBase();

  const [agencies, setAgencies] = useState([]);
  const [contributions, setContributions] = useState([]);
  const [listingSync, setListingSync] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [contributeOpen, setContributeOpen] = useState(false);
  const [syncSaving, setSyncSaving] = useState(false);

  const loadCore = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // Keep this page snappy: sync prefs + contribution history only.
      // Agency list is heavy (listing aggregates) — load lite on demand for the wizard.
      const [contribBody, syncBody] = await Promise.all([
        fetchCasaMapaPartnerDataContributions({ limit: 12 }).catch(() => ({
          contributions: [],
        })),
        fetchCasaMapaPartnerListingSync().catch(() => null),
      ]);
      setContributions(contribBody.contributions || []);
      setListingSync(syncBody);
    } catch (err) {
      setError(err.message || t(`${COPY}.loadError`));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadAgenciesLite = useCallback(async () => {
    try {
      const agenciesBody = await fetchCasaMapaPartnerAgencies({ lite: "1" });
      setAgencies(agenciesBody.agencies || []);
    } catch {
      setAgencies([]);
    }
  }, []);

  useEffect(() => {
    loadCore();
  }, [loadCore]);

  useEffect(() => {
    if (!contributeOpen) return undefined;
    if (agencies.length) return undefined;
    loadAgenciesLite();
    return undefined;
  }, [contributeOpen, agencies.length, loadAgenciesLite]);

  const syncMode = listingSync?.mode || "live";
  const agencyOptions = useMemo(
    () =>
      (agencies || []).map((agency) => ({
        slug: agency.slug,
        name: agency.name || agency.displayName || agency.slug,
      })),
    [agencies]
  );

  const saveSyncMode = async (mode) => {
    setSyncSaving(true);
    setError("");
    try {
      const result = await updateCasaMapaPartnerListingSync({
        mode,
        minConfidence: listingSync?.prefs?.minConfidence,
      });
      setListingSync(result);
      setMessage(t(`${COPY}.inventory.syncSaved`));
    } catch (err) {
      setError(err.message || t(`${COPY}.saveError`));
    } finally {
      setSyncSaving(false);
    }
  };

  const openContribute = () => {
    setContributeOpen(true);
  };

  return (
    <PartnerCasaMapaSettingsShell
      title={t(`${COPY}.inventory.title`)}
      subtitle={t(`${COPY}.inventory.subtitle`)}
      message={message}
      error={error}
    >
      {loading ? <p className="cm-partner-settings__muted">{t(`${COPY}.loading`)}</p> : null}

      <section className="cm-partner-settings__section">
        <div className="cm-partner-settings__actions">
          <button
            type="button"
            className="partner-dashboard-btn partner-dashboard-btn--primary"
            onClick={openContribute}
          >
            {t(`${COPY}.inventory.connect`)}
          </button>
          <Link className="partner-dashboard-btn" to={path("listings")}>
            {t(`${COPY}.inventory.openListings`)}
          </Link>
        </div>

        <div>
          <h3 style={{ margin: "0 0 0.55rem", fontSize: "0.95rem" }}>
            {t(`${COPY}.inventory.syncTitle`)}
          </h3>
          <div
            className="cm-partner-settings__sync-modes"
            role="radiogroup"
            aria-label={t(`${COPY}.inventory.syncTitle`)}
          >
            {[
              { id: "off", label: t(`${COPY}.inventory.sync.off`), hint: t(`${COPY}.inventory.sync.offHint`) },
              { id: "live", label: t(`${COPY}.inventory.sync.live`), hint: t(`${COPY}.inventory.sync.liveHint`) },
              {
                id: "review",
                label: t(`${COPY}.inventory.sync.review`),
                hint: t(`${COPY}.inventory.sync.reviewHint`),
              },
            ].map((option) => (
              <button
                key={option.id}
                type="button"
                className={`cm-partner-settings__sync-card${syncMode === option.id ? " is-active" : ""}`}
                aria-pressed={syncMode === option.id}
                disabled={syncSaving || loading}
                onClick={() => saveSyncMode(option.id)}
              >
                <strong>{option.label}</strong>
                <span>{option.hint}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <h3 style={{ margin: "0 0 0.55rem", fontSize: "0.95rem" }}>
            {t(`${COPY}.inventory.historyTitle`)}
          </h3>
          {!loading && contributions.length === 0 ? (
            <p className="cm-partner-settings__muted">{t(`${COPY}.inventory.historyEmpty`)}</p>
          ) : null}
          {contributions.length > 0 ? (
            <ul className="cm-partner-settings__contrib-list">
              {contributions.map((row) => (
                <li key={row.contributionId}>
                  <div>
                    <strong>{t(`${COPY}.inventory.methods.${row.method}`)}</strong>
                    <span className="cm-partner-settings__muted">
                      {" · "}
                      {formatPartnerSettingsWhen(row.createdAt) || "—"}
                      {row.agencySlug ? ` · ${row.agencySlug}` : ""}
                      {row.fileName ? ` · ${row.fileName}` : ""}
                    </span>
                  </div>
                  <span className={partnerSettingsStatusBadgeClass(row.status)}>{row.status}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </section>

      <PartnerListingDataContributionWizard
        open={contributeOpen}
        onClose={() => {
          setContributeOpen(false);
          loadCore();
        }}
        agencies={agencyOptions}
        defaultAgencySlug={agencyOptions[0]?.slug || ""}
      />
    </PartnerCasaMapaSettingsShell>
  );
}
